import { motion } from "framer-motion";
import { Layers, Bot, ShieldCheck, ArrowRight } from "lucide-react";
import { useTranslation } from "@/i18n/useTranslation";

const phaseIcons = [Layers, Bot, ShieldCheck];

const ease = [0.16, 1, 0.3, 1] as const;

const RoadmapSection = () => {
  const { t } = useTranslation();

  return (
    <section id="roadmap" className="section-padding bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <p className="label-text mb-3">{t.roadmap.label}</p>
          <h2 className="font-display font-bold text-foreground mb-4">
            {t.roadmap.heading}
          </h2>
          <p className="text-muted-foreground leading-relaxed font-body">
            {t.roadmap.subheading}
          </p>
        </motion.div>

        <div className="relative max-w-4xl mx-auto">
          <div className="hidden md:block absolute left-8 top-0 bottom-0 w-px bg-border" />

          <div className="flex flex-col gap-8 md:gap-6">
            {t.roadmap.phases.map((p, i) => {
              const Icon = phaseIcons[i];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.12, ease }}
                  className="relative flex flex-col md:flex-row gap-5 md:gap-8"
                >
                  <div className="hidden md:flex flex-col items-center shrink-0 w-16">
                    <div className="w-4 h-4 rounded-full bg-primary border-4 border-background z-10" />
                  </div>

                  <div
                    className="group flex-1 bg-card border border-border rounded-xl p-6 md:p-7 glow-border"
                    style={{ boxShadow: "var(--shadow-card)" }}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-4">
                      <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                        <Icon size={22} className="text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-1">
                          <span className="text-xs font-semibold text-primary font-display uppercase tracking-wider">
                            {p.phase}
                          </span>
                          <span className="text-xs text-muted-foreground font-body">
                            {p.period}
                          </span>
                        </div>
                        <h3 className="font-display font-semibold text-foreground text-lg">
                          {p.title}
                        </h3>
                      </div>
                    </div>

                    <p className="text-muted-foreground text-sm leading-relaxed font-body mb-4">
                      {p.desc}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {p.highlights.map((h) => (
                        <span
                          key={h}
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-secondary/60 border border-border rounded-full px-3 py-1 font-body"
                        >
                          <ArrowRight size={10} className="text-primary" />
                          {h}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="hidden md:flex justify-start pl-[26px] pt-2">
            <div className="w-2.5 h-2.5 rounded-full bg-primary/40" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoadmapSection;
